import axios from 'axios'
import { getToken } from '../../auth/auth'
import { removeODataContext, strToB64 } from '../utils'
import { ApiGateway } from '../../config/config'
import FormData from "form-data";
import createRetryAxios from './axiosRetry'
import Cache, { makeCacheKey }  from '../../cache/Cache';
import { getSiteConfig } from '../../community/siteProvider'
import { Stage } from '../../config/config';

export * from './documents'

export const getAccessToken = async () => {
  const local = window.sessionStorage.mockGraphToken
  if (local) return local
  return (await getToken()).accessToken
  // clearMinorToken()
  // const elevatedToken = await getToken(false, ['Files.ReadWrite.All', 'Sites.ReadWrite.All'])
  // return elevatedToken.accessToken
}

export const eTagToGuid = (eTag) => {
  return eTag?.toLowerCase()?.replace(/[{}"']/gi, '')?.split(',')?.[0]
}

export const decodeSharePointText = (encodedStr) => {
  if (!encodedStr) return encodedStr;
  
  // Replace SharePoint-specific encoding
  return encodedStr
    .replace(/&amp;/g, '&') // Decode ampersand
    .replace(/&lt;/g, '<')  // Decode less-than
    .replace(/&gt;/g, '>')  // Decode greater-than
    .replace(/&quot;/g, '"') // Decode double quote
    .replace(/&apos;/g, "'") // Decode single quote
    .replace(/&#58;/g, ':'); // Decode SharePoint-encoded colon
}

export const makeListArtclesByCategoryRequest = (category, limit = 200) => {
  const { siteId, listIds } = getSiteConfig()
  const filters = [`fields/QSEPortalOUCornerCategory+eq+'${encodeURIComponent(category)}'`]
  const orderBy = '&$orderby=lastModifiedDateTime desc'
  const path = `/sites/${siteId}/lists/${listIds.pages}/items?$expand=fields&$filter=${filters.join('+and+')}&$top=${limit}${orderBy}`
  const baseUrl = 'https://graph.microsoft.com/v1.0'
  const method = 'GET'
  return { path, baseUrl, method }
}

export const makeListArtclesByVisibilityRequest = (visibility, nextToken, limit = 10, pinned = false) => {
  const { siteId, listIds } = getSiteConfig()
  if(nextToken) return {
    baseUrl: 'https://graph.microsoft.com/v1.0',
    path: nextToken.replace('https://graph.microsoft.com/v1.0', ''),
    method: 'GET'
  }
  
  const {
    ouCorner,
    highlighted,
    ouCornerCategory,
    news,
    featured
  } = visibility
  
  const filters = []
  if(ouCorner) filters.push('fields/QSEPortalOUCorner+eq+1')
  if(highlighted) filters.push('fields/QSEPortalHighlighted+eq+1')
  if(ouCornerCategory && ouCornerCategory !== 'Highlighted') filters.push(`fields/QSEPortalOUCornerCategory+eq+'${encodeURIComponent(ouCornerCategory)}'`)
  if(news) {
    filters.push('fields/QSEPortalNews+eq+1')
    filters.push(`fields/QSEPortalFeatured+${featured ? 'eq' : 'ne'}+1`)
  }
  if(pinned) {
    filters.push(`(fields/QSEPortalPinPositions+ne+''+and+fields/QSEPortalPinPositions+ne+null)`)
  } else {
    filters.push(`(fields/QSEPortalPinPositions+eq+''+or+fields/QSEPortalPinPositions+eq+null)`)
  }

  const orderBy = '&$orderby=lastModifiedDateTime desc'
  const path = `/sites/${siteId}/lists/${listIds.pages}/items?$expand=fields&$filter=${filters.join('+and+')}&$top=${limit}${orderBy}`
  const baseUrl = 'https://graph.microsoft.com/v1.0'
  const method = 'GET'
  return { path, baseUrl, method }
}

export const makeSearchArticlesHavingTagsRequest = (tags) => {
  const { urls } = getSiteConfig()
  const filters = tags?.map(tag => {
    return `"tag:${tag}"`
  }) || []
  const path = `/search/query`
  const baseUrl = 'https://graph.microsoft.com/v1.0'
  const method = 'POST'
  const body = {
    "requests": [
      {
        "entityTypes": [
          "listItem"
        ],
        "query": {
          "queryString": `${filters.join(' AND ')} path:"${urls.baseSite}/SitePages" AND NOT path:"${urls.baseSite}/SitePages/Templates"`
        },
        "sortProperties": [
          {
            "name": "createdDateTime",
            "isDescending": true
          }
        ]
      }
    ]
  }
  const headers = {
    'Content-Type': 'application/json'
  }
  return { path, baseUrl, method, body, headers }
}

export const makeSearchArticlesInCategoryRequest = (category) => {
  const { urls } = getSiteConfig()
  const filters = [`"visibility:OUCorner::${category}"`]
  const path = `/search/query`
  const baseUrl = 'https://graph.microsoft.com/v1.0'
  const method = 'POST'
  const body = {
    "requests": [
      {
        "entityTypes": [
          "listItem"
        ],
        "query": {
          "queryString": `${filters.join(' AND ')} path:"${urls.baseSite}/SitePages" AND NOT path:"${urls.baseSite}/SitePages/Templates"`
        },
        "sortProperties": [
          {
            "name": "createdDateTime",
            "isDescending": true
          }
        ]
      }
    ]
  }
  const headers = {
    'Content-Type': 'application/json'
  }
  return { path, baseUrl, method, body, headers }
}

export const makeSearchResourcesHavingTagsRequest = (tags) => {
  const { urls } = getSiteConfig()
  const filters = tags?.map(tag => {
    return `"tag:${tag}"`
  }) || []
  const path = `/search/query`
  const baseUrl = 'https://graph.microsoft.com/v1.0'
  const method = 'POST'
  const body = {
    "requests": [
      {
        "entityTypes": [
          "listItem"
        ],
        "query": {
          "queryString": `${filters.join(' AND ')} path:"${urls.baseSite}/Shared Documents" AND NOT path:"${urls.baseSite}/Shared Documents/Settings" AND NOT path:"${urls.baseSite}/Shared Documents/Users"`
        },
        "sortProperties": [
          {
            "name": "createdDateTime",
            "isDescending": true
          }
        ]
      }
    ]
  }
  const headers = {
    'Content-Type': 'application/json'
  }
  return { path, baseUrl, method, body, headers }
}

export const makeSearchResourcesInCategoryRequest = (category) => {
  const { urls } = getSiteConfig()
  const filters = [`"category:${category}"`]
  const path = `/search/query`
  const baseUrl = 'https://graph.microsoft.com/v1.0'
  const method = 'POST'
  const body = {
    "requests": [
      {
        "entityTypes": [
          "listItem"
        ],
        "query": {
          "queryString": `${filters.join(' AND ')} path:"${urls.baseSite}/Shared Documents" AND NOT path:"${urls.baseSite}/Shared Documents/Settings" AND NOT path:"${urls.baseSite}/Shared Documents/Users"`
        },
        "sortProperties": [
          {
            "name": "createdDateTime",
            "isDescending": true
          }
        ]
      }
    ]
  }
  const headers = {
    'Content-Type': 'application/json'
  }
  return { path, baseUrl, method, body, headers }
}

export const makeSearchArticlesRequest = (tags, visibility, startIndex = 0, limit = 10, pinned = false) => {
  const { urls } = getSiteConfig()
  const tagFilters = tags?.map(tag => {
    return `"tag:${tag}"`
  }) || []
  
  const visibilityFilters = []
  if(visibility.news) {
    visibilityFilters.push('"visibility:News"')
    visibilityFilters.push(`${visibility.featured ? '' : 'NOT '}"visibility:Featured"`)
  }
  if(visibility.ouCornerCategory && visibility.ouCornerCategory !== 'Highlighted')
    visibilityFilters.push(`"visibility:OUCorner::${visibility.ouCornerCategory}"`)
  if(visibility.highlighted)
    visibilityFilters.push(`"visibility:OUCorner::Highlighted"`)

  const allFilters = [...tagFilters, ...visibilityFilters]
  // if(pinned) {
  //   allFilters.push('"pinned:"')
  // } else {
  //   allFilters.push('NOT "pinned:"')
  // }
  const path = `/search/query`
  const baseUrl = 'https://graph.microsoft.com/v1.0'
  const method = 'POST'
  const body = {
    "requests": [
      {
        "entityTypes": [
          "listItem"
        ],
        "query": {
          "queryString": `${allFilters.join(' AND ')} path:"${urls.baseSite}/SitePages" AND NOT path:"${urls.baseSite}/SitePages/Templates"`
        },
        "sortProperties": [
          {
            "name": "createdDateTime",
            "isDescending": true
          }
        ],
        from: startIndex,
        size: limit
      }
    ]
  }
  const headers = {
    'Content-Type': 'application/json'
  }
  return { path, baseUrl, method, body, headers }
}

export const makeGetArticleSitePageByIdRequest = (pageId) => {
  const { siteId } = getSiteConfig()
  const path = `/sites/${siteId}/pages/${pageId}/microsoft.graph.sitePage`
  const baseUrl = 'https://graph.microsoft.com/v1.0'
  const method = 'GET'
  return { path, method, baseUrl }
}

export const makeGetListItemByIdRequest = (listId, listItemId, filters = []) => {
  const { siteId } = getSiteConfig()
  const _filters = filters.length ? '$filters='+filters.join(' and ') : ''
  const path = `/sites/${siteId}/lists/${listId}/items/${listItemId}?$expand=fields&${_filters}`
  const baseUrl = 'https://graph.microsoft.com/v1.0'
  const method = 'GET'
  return { path, method, baseUrl }
}

export const makeGetArticleListItemByIdRequest = (listItemId, filters = []) => {
  const { listIds } = getSiteConfig()
  const request = makeGetListItemByIdRequest(listIds.pages, listItemId, filters)
  return request
}

export const makeGetResourceListItemByIdRequest = (listItemId, filters = []) => {
  const { listIds } = getSiteConfig()
  const request = makeGetListItemByIdRequest(listIds.documentLibrary, listItemId, filters)
  return request
}

export const makeBatchRequest = (requests, version = 'v1.0') => {
  const body = { requests }
  const path = '/$batch'
  const baseUrl = `https://graph.microsoft.com/${version}`
  const method = 'POST'
  return { path, baseUrl, method, body }
}

function chunk(arr, size = 20) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

export async function sendRequest(request, accessToken = undefined) {
  const token = accessToken ?? await getAccessToken();
  const url   = `${request.baseUrl}${request.path}`;
  const cfg   = { headers: { Authorization: `Bearer ${token}` } };
  const requestLimit = 20
  /* ─────────────────────────────
   *  Special case: Graph $batch
   * ───────────────────────────── */
  const isBatch =
    request.path === "/$batch" &&
    Array.isArray(request.body?.requests);

  if (isBatch) {
    const all = request.body.requests;

    /* If ≤ 20, just send it and return */
    if (all.length <= requestLimit) {
      // const { data } = await axios.post(url, request.body, cfg);

      const response = await createRetryAxios().post(url, request.body, cfg);
      const data = response?.data
      return data;                       // { responses: [...] }
    }
    /* > 20: send sequential 20-request chunks */
    const combined = [];
    for (const reqChunk of chunk(all, requestLimit)) {
      const { data } = await axios.post(
        url,
        { requests: reqChunk },
        cfg
      );
      combined.push(...data.responses);
    }
    return { responses: combined };
  }

  /* ─────────────────────────────
   *  Regular (non-batch) request
   * ───────────────────────────── */
  const args = [url];
  if (request.body) args.push(request.body);
  args.push(cfg);

  const response = await createRetryAxios()[request.method.toLowerCase()](...args);
  const data = response?.data
  return data;
}

export const makeGetNewsLinkPageByIdRequest = (id) => {
  const { siteId } = getSiteConfig()
  const path = `/sites/${siteId}/pages/${id}/microsoft.graph.newsLinkPage`
  const baseUrl = 'https://graph.microsoft.com/beta'
  const method = 'GET'
  return { path, method, baseUrl }
}

export async function* getArticlesByTags(args) {
  const {visibility, tags = [], startIndex = 0, limit = 10 } = args
  const token = await getAccessToken()
  const options = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  }

  // Need to use Search for articles
  // Search result has only total size and list ids
  // const searchPinnedArticlesRequest = makeSearchArticlesRequest(tags, visibility, startIndex, limit, true)
  const searchArticlesRequest = makeSearchArticlesRequest(tags, visibility, startIndex, limit, false)
  const cacheKey = `get-articles-by-tags-search-${strToB64(JSON.stringify({visibility, tags, startIndex, limit}))}`
  const cacheHit = Cache.getItem(cacheKey)

  if(cacheHit) {
    yield cacheHit
    return
  }
  
  const { baseUrl, path, body, method } = searchArticlesRequest
  // const { baseUrl: urlPinned, path: pathPinned, body: bodyPinned, method: methodPinned } = searchPinnedArticlesRequest

  const [
    response,
    // responsePinned
  ] = await Promise.all([
     createRetryAxios()[method.toLowerCase()](baseUrl + path, body, options),
    // createRetryAxios()[methodPinned.toLowerCase()](urlPinned + pathPinned, bodyPinned, options)
  ])

  // Get non-pinned articles
  const data = response?.data

  // Get pinned articles
  // const dataPinned = responsePinned?.data

  if(!data) {
    yield {articles: []}
    return
  }

  const parsedSearchResponse = parseSearchResponse(data.value)
  // const parsedPinnedSearchResponse = parseSearchResponse(dataPinned.value)

  // parsedSearchResponse.results.push(...parsedPinnedSearchResponse.results)
  const { results: _results, totalSize } = parsedSearchResponse

  if (!_results.length) {
    yield {articles: []}
    return
  }

  yield { totalSize }
  // Create batch requests for full listItem and sitePage entities
  const batchBody = { requests: [] }
  let id = 1
  for (const item of _results) {
    const pageRequest = makeGetArticleSitePageByIdRequest(item.hitId)
    const listItemRequest = makeGetArticleListItemByIdRequest(item.hitId)
    batchBody.requests.push({
      id: `${id++}`,
      method: pageRequest.method,
      url: pageRequest.path
    })
    batchBody.requests.push({
      id: `${id++}`,
      method: listItemRequest.method,
      url: listItemRequest.path
    })
  }
  const batchRequest = makeBatchRequest(batchBody.requests)


  const batchResponse = await sendRequest(batchRequest)

  const { responses } = batchResponse
  responses.sort((a, b) => parseInt(a.id) - parseInt(b.id))

  const articles = []
  for (let i = 0; i < responses?.length; i += 2) {
    const sitePage = responses[i];
    const listItem = responses[i + 1];
    const sitePageIsPossiblyNewsLinkPage = sitePage.status === 400 && sitePage.body?.error?.code === "invalidRequest"
    if(!sitePage || (!sitePageIsPossiblyNewsLinkPage && sitePage.status !== 200) || !listItem || listItem.status !== 200) continue
    if(listItem.body.contentType.name === 'Repost Page') {
      const newsLinkPageRequest = makeGetNewsLinkPageByIdRequest(listItem.body.eTag.replace(/"/g, '').split(',')[0])
      const response = await createRetryAxios()[newsLinkPageRequest.method.toLowerCase()](
        newsLinkPageRequest.baseUrl + newsLinkPageRequest.path, options
      )
      const data = response?.data
      if(!data) continue
      sitePage.body = data
    }
    const mapped = await mapPage({
      ...sitePage.body,
      fields: listItem.body.fields,
    }, Cache, true, visibility)
    articles.push(mapped)
  }

  const nonPinnedArticles = articles.filter(a => a.pinPosition === undefined)
  // const pinnedArticles = articles.filter(a => a.pinPosition !== undefined)
  // pinnedArticles.sort((a,b) => a.pinPosition - b.pinPosition)

  const sortedArticles = nonPinnedArticles //insertPinned(nonPinnedArticles, pinnedArticles)
  // [...pinnedArticles, ...nonPinnedArticlse]

  Cache.setItem(cacheKey, {
    articles: sortedArticles,
    totalSize
  })

  yield {
    articles: sortedArticles,
    totalSize
  }

}

let defaultArticleThumbnailUrl
export const getDefaultArticleThumbnailUrl = async () => {
  const { siteId, driveIds, defaultImagePath } = getSiteConfig()
  if(defaultArticleThumbnailUrl) return defaultArticleThumbnailUrl

  const token = await getAccessToken()
  const options = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  }
  try {
    const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveIds.SiteAssets}/root:/${defaultImagePath}`
    const { data } = await axios.get(url, options)
    defaultArticleThumbnailUrl = data['@microsoft.graph.downloadUrl']
    return defaultArticleThumbnailUrl
  } catch(err) {
    console.error(err)
  }
}


export const isDraft = (sitePage) => {
  if(sitePage.publishingState?.level === 'checkout' && sitePage.publishingState.versionId === '0.1') return true
  return sitePage.publishingState?.level === 'draft'
}

// Need this in case there are any non-latin1 characters present
function base64EncodeUnicode(str) {
  const bytes = new TextEncoder().encode(str); // UTF-8 → Uint8Array
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

function toGraphSharesToken(url) {
  // URL → base-64 (plain btoa is OK because SharePoint URLs are pure ASCII)
  let b64 = base64EncodeUnicode(url);

  // base-64 → base64url (RFC-4648 §5, the format Graph expects)
  b64 = b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

  // prepend the magic "u!" prefix
  return `u!${b64}`;
}

export const getPathGuidOrUrl = (imageUrl) => {
  if(!imageUrl) return
  // media.akami urls throw a 403 if you're not logged into sharepoint
  // But the sharepoint hosted image is public so remove media.akamai... domain
  if(/https:\/\/media.akamai.odsp.cdn.office.net\/groups.coca-cola.com/.test(imageUrl)) {
    const value = imageUrl.replace(/media.akamai.odsp.cdn.office.net/, '')
    return { type: 'url', value }
  }

  if(/https:\/\/(groups|onedrive)\.coca-cola\.com/i.test(imageUrl)) {
    if(/\/getpreview.as(h|p)x/i.test(imageUrl)) {
      if(/(\?|&)path=/i.test(imageUrl)) {
        const pathValue = decodeURIComponent(imageUrl.split('path=')[1].split('&')[0])
        return { type: 'path', value: pathValue }
      } else if(/(\?|&)guidfile=/i.test(imageUrl)) {
        const guidFile = imageUrl.split('guidFile=')[1].split('&')[0]
        return { type: 'guid', value: guidFile }
      }
    } else if(/groups\.coca-cola\.com\/sites\//.test(imageUrl)){
      return { type: 'path', value: imageUrl }
    }
  } else {
    return { type: 'url', value: imageUrl
      .replace(/resolution=[0-9]{1}/i, 'resolution=3')
      .replace(/large\.jpg/, 'xxlarge.jpg')
    }
  }
}

export const makeGetDriveItemByFQDNRequest = (url) => {
  const request = {}
  const shareToken = toGraphSharesToken(url);
  request.path = `/shares/${shareToken}/driveItem?$expand=thumbnails`
  request.method = 'GET'
  request.baseUrl = 'https://graph.microsoft.com/v1.0'
  return request
}

export const getDriveItemByFQDN = async (url) => {
  const request = makeGetDriveItemByFQDNRequest(url)
  const result = await sendRequest(request)
  return result
}

export const makeGetDriveItemByWebUrlRequest = (imageUrl) => {
  const { domain, siteId, driveIds } = getSiteConfig()
  let path, guid
  const pathGuidOrUrl = getPathGuidOrUrl(imageUrl)

  if(pathGuidOrUrl?.type === 'url') return pathGuidOrUrl.value

  if(pathGuidOrUrl?.type === 'path') path = pathGuidOrUrl.value
  if(pathGuidOrUrl?.type === 'guid') guid = pathGuidOrUrl.value

  
  const request = {}
  if(path) {
    const fullPath = `${domain}${path.replace(domain, '')}`
    const shareToken = toGraphSharesToken(fullPath);
    request.path = `/shares/${shareToken}/driveItem`
  } else if(guid) {
    request.path = `/sites/${siteId}/drives/${driveIds.SiteAssets}/items/${guid}`
  }

  request.method = 'GET'
  request.baseUrl = 'https://graph.microsoft.com/v1.0'
  return request
}

export const getThumbnailPresignedUrl = async (path, guid) => {
  const { domain, siteId, driveIds } = getSiteConfig()
  const token = await getAccessToken()
  const options = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  }

  try {
    if(path) {
      const fullPath = `${domain}${path.replace(domain, '')}`
      const token  = toGraphSharesToken(fullPath);

      const driveItem = await axios.get(
        `https://graph.microsoft.com/v1.0/shares/${token}/driveItem`,
        options
      );
      return driveItem.data['@microsoft.graph.downloadUrl']
    } else {
      const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveIds.SiteAssets}/items/${guid}`
      const { data } = await axios.get(url, options)
      return data['@microsoft.graph.downloadUrl']
    }
  } catch(err) {
    console.error(err)
    return await getDefaultArticleThumbnailUrl()
  }
}

export async function fetchAndCacheThumbnail(key, presignedUrlGetter, cache) {
  if (cache && cache.getItem(key)) return cache.getItem(key);   // early-out

  try {
    const presignedUrl = await presignedUrlGetter()
    const res  = await fetch(presignedUrl);
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    if(cache) cache.setItem(key, url);
    return url;
  } catch(err) {
    console.log(err)
  }
}

export const getThumbnailImage = async (newsPost, cache) => {

  const imageUrl = decodeURI(newsPost.thumbnailWebUrl || newsPost.bannerImageWebUrl || '')
  if(!imageUrl) return await getDefaultArticleThumbnailUrl()


  if(/https:\/\/(groups|onedrive)\.coca-cola\.com/i.test(imageUrl)) {
    if(/\/getpreview.as(h|p)x/i.test(imageUrl)) {
      if(/(\?|&)path=/i.test(imageUrl)) {
        const pathValue = decodeURIComponent(imageUrl.split('path=')[1].split('&')[0])
        if(pathValue) {
          const url = await fetchAndCacheThumbnail(
            `thumbnail-by-path-${pathValue}`,
            async () => await getThumbnailPresignedUrl(pathValue, null),
            cache,
          )
          return url
        }
      } else if(/(\?|&)guidfile=/i.test(imageUrl)) {
        const guidFile = newsPost.thumbnailWebUrl.split('guidFile=')[1].split('&')[0]
        const url = await fetchAndCacheThumbnail(
          `thumbnail-by-guid-${guidFile}`,
          async () => await getThumbnailPresignedUrl(null, guidFile),
          cache,
        )
        return url
      }
    } else if(/groups\.coca-cola\.com\/sites\//.test(imageUrl)){
      const url = await fetchAndCacheThumbnail(
        `thumbnail-by-path-${imageUrl}`,
        async () => await getThumbnailPresignedUrl(imageUrl),
        cache,
      )
      return url
    }
  } else {
    return imageUrl
      .replace(/resolution=[0-9]{1}/i, 'resolution=3')
      .replace(/large\.jpg/, 'xxlarge.jpg')
  }
}

const extractPinPositions = (article) => {
  const pinPositions = article?.fields?.QSEPortalPinPositions?.split(',')?.reduce((s, p) => {
    const parts = p.replace(/^pinned:/, '').split(':');
    const position = parseInt(parts.pop(), 10); // last part is number
    const key = parts.join(':'); // join remaining parts for the label
    s[key] = position;
    return s;
  }, {})
  return pinPositions
}

export const mapPage = async (newsPost, cache, resolveBannerImage = false, visibility) => {
  let image
  if(resolveBannerImage) {
    image = await getThumbnailImage(newsPost, cache)
  } else {
    const pathGuidOrUrlResult = getPathGuidOrUrl(newsPost.thumbnailWebUrl || newsPost.bannerImageWebUrl)
    if(pathGuidOrUrlResult?.type === 'url') image = pathGuidOrUrlResult.value
    else image = newsPost.thumbnailWebUrl || newsPost.bannerImageWebUrl
  }


  const pinPositions = extractPinPositions(newsPost)
  const [pinKey] = getPinKeysFromVisibiity(visibility)
  const isPinned = pinPositions?.[pinKey] >= 0
  const pinPosition = pinPositions?.[pinKey]

  const metadata = {
    featured: newsPost?.fields?.QSEPortalFeatured,
    news: newsPost?.fields?.QSEPortalNews,
    ouCorner: newsPost?.fields?.QSEPortalOUCorner,
    highlighted: newsPost?.fields?.QSEPortalHighlighted,
    ouCornerCategory: newsPost?.fields?.QSEPortalOUCornerCategory,
    tags: newsPost?.fields?.QSEPortalTags?.split(',')?.filter(t => t?.indexOf('tag:') === 0)?.map(t => t?.split('tag:')?.[1]),
    title: newsPost.title,
    isPinned,
    pinPosition,
    pinPositions
  }


  return {
    id: newsPost.id,
    title: newsPost.title || newsPost?.fields?.Title,
    description: newsPost.description,
    author: newsPost?.createdBy?.user?.displayName,
    date: new Date(newsPost.createdDateTime).toLocaleDateString(),
    createdTimestamp: new Date(newsPost.createdDateTime).getTime(),
    image,
    originalImage: newsPost.thumbnailWebUrl || newsPost.bannerImageWebUrl,
    webUrl: newsPost.webUrl,
    newsWebUrl: newsPost.newsWebUrl,
    isDraft: isDraft(newsPost),
    tags: newsPost?.fields?.QSEPortalTags ?? '',
    guid: eTagToGuid(newsPost.eTag),
    contentType: newsPost?.contentType?.name,
    metadata
  }
}

export const parseSearchResponse = (value) => {
  return {
    results: value?.[0]?.hitsContainers?.[0]?.hits || [],
    totalSize: value?.[0]?.hitsContainers?.[0]?.total
  }
}

// This method mutates the incoming articles adding the downloaded image
// then returns the mutated articles
export const addDownloadUrlsToArticles = async (articles, cache) => {
  if(!cache) throw new Error('not providing the cache')
  // batch fetch driveitems
  const driveItemRequests = articles?.reduce((s, article, id) => {
    const request = makeGetDriveItemByWebUrlRequest(article.image)
    if(request && typeof request !== 'string') {
      if(request.path && request.method) {
        s.push({
          url: request.path,
          method: request.method,
          id: `${article.guid}`
        })
      }
    }
    return s
  }, [])

  if(!driveItemRequests?.length) return articles

  const driveItemBatchRequest = makeBatchRequest(driveItemRequests)
  const { responses: driveItems } = await sendRequest(driveItemBatchRequest)
  const articlesWithImages = articles.map(article => {
    const di = driveItems.find(di => di.id === article.guid)
    if(di) article.image = di.body["@microsoft.graph.downloadUrl"]
    return article
  })
  return articlesWithImages
}

export function buildPinnedKey(visibility) {
  // Check News path
  if (visibility.news) {
    if (visibility.featured) {
      return 'News:Featured';
    }
    return 'News';
  }

  // Check OU Corner path
  if (visibility.ouCorner) {
    if (visibility.highlighted) {
      return 'OUCorner:Highlighted';
    }
    if (visibility.ouCornerCategory) {
      // Assume ouCornerCategory contains the category string
      return `OUCorner:${visibility.ouCornerCategory}`;
    }
    return 'OUCorner';
  }

  // If no match, return undefined or empty string
  return '';
}

export async function getPinnedArticles(visibility, cache) {
  const v = visibility
  const cacheKey = `pinned-articles-${v.news}${v.featured}${v.ouCorner}${v.highlighted}${v.ouCornerCategory}`
  const cacheHit = cache.getItem(cacheKey)

  if(cacheHit) {
    return cacheHit
  } else {
    const request = makeListArtclesByVisibilityRequest(visibility, null, 20, true)
    const results = await sendRequest(request)

    // There will always be one key since this function is provided with only one visibility
    const [pinPositionKey] = getPinKeysFromVisibiity(visibility)
   
    const pinnedMap = results?.value?.reduce((s, article) => {
      const pinPositions = extractPinPositions(article)
      // Articles can be pinned, but not pinnned in this category
      const match = parseInt(pinPositions[pinPositionKey]) >= 0
      if(match) {
        s[pinPositions[pinPositionKey]] = true
      }
      return s
    }, {})
    cache.setItem(cacheKey, pinnedMap)
    return pinnedMap // ex: {0: true, 2: true}
  }
}

const insertPinned = (baseArticles, pinnedArticles, pinPositionKey) => {
  if (!pinnedArticles.length) return baseArticles;

  const getPinPosition = (article) => {
    const found = article?.fields?.QSEPortalPinPositions
      ?.split(',')
      ?.find(f => f.startsWith(`pinned:${pinPositionKey}`));
    const parsed = parseInt(found?.split(':')?.pop(), 10);
    return isNaN(parsed) ? null : parsed;
  };

  // Ensure all pinned articles have numeric positions; fallback to sequential after base end
  let validatedPins = pinnedArticles.map((item, i) => {
    const pos = getPinPosition(item);
    return {
      ...item,
      _pinPos: pos !== null ? pos : baseArticles.length + i
    };
  });

  // Special case: no base articles → reassign pin positions sequentially starting at 0
  if (baseArticles.length === 0) {
    validatedPins = validatedPins.map((pin, i) => ({ ...pin, _pinPos: i }));
  }

  // Sort pins by their position
  validatedPins.sort((a, b) => a._pinPos - b._pinPos);

  const result = [];
  let baseIdx = 0;
  let pinIdx = 0;

  const maxPos = Math.max(
    baseArticles.length + validatedPins.length,
    ...validatedPins.map(p => p._pinPos)
  );

  for (let pos = 0; pos < maxPos; pos++) {
    if (pinIdx < validatedPins.length && validatedPins[pinIdx]._pinPos === pos) {
      result.push(validatedPins[pinIdx++]);
    } else if (baseIdx < baseArticles.length) {
      result.push(baseArticles[baseIdx++]);
    } else if (pinIdx < validatedPins.length) {
      // Fill gaps with remaining pins if base articles are exhausted
      result.push(validatedPins[pinIdx++]);
    }
  }

  return result;
};

const filterArticlesOnPublished = (articles = [], published) => {
  return articles?.filter(listItem => {
    const isPublished = !listItem.fields._UIVersionString?.startsWith('0.')
    if(published) return isPublished
    if(!published) return !isPublished
    return true
  })
}

export const getPinKeysFromVisibiity = (visibility) => {
  const keys = []
  if (visibility.news && visibility.featured) keys.push('News:Featured')
  if (visibility.news && !visibility.featured) keys.push('News')
  if (visibility.ouCorner && visibility.highlighted) keys.push('OUCorner:Highlighted')
  if (visibility.ouCorner && !visibility.highlighted && visibility.ouCornerCategory) keys.push(`OUCorner:${visibility.ouCornerCategory}`)
  return keys
}

const fieldsMatchVisibility = (fields, visibility) => {
  if(visibility.news && visibility.featured && fields.QSEPortalNews && fields.QSEPortalFeatured) return true
  if(visibility.news && fields.QSEPortalNews) return true
  if(visibility.ouCorner && visibility.highlighted && fields.QSEPortalOUCorner && fields.QSEPortalHighlighted) return true
  if(visibility.ouCorner && fields.QSEPortalOUCorner && visibility.ouCornerCategory === fields.QSEPortalOUCornerCategory) return true
  return false
};

export function getListArticlesPageFromCache(visibility = {}, published, startIndex = 0, nextToken, limit = 3) {
  const cacheKeyLatest = `list-articles-by-visibility-latest-${makeCacheKey({visibility,published,startIndex,nextToken,limit})}`
  const cacheHitLatest = Cache.getItem(cacheKeyLatest)
  return cacheHitLatest
}

export async function* listArticles(visibility = {}, published, startIndex = 0, nextToken, limit = 3) {
  const cache = Cache
  const cacheKey1 = `list-articles-by-visibility-${makeCacheKey({visibility, published})}`
  const cacheHit = cache.getItem(cacheKey1)

  const cacheKeyLatest = `list-articles-by-visibility-latest-${makeCacheKey({visibility,published,startIndex,nextToken,limit})}`
  const cacheHitLatest = Cache.getItem(cacheKeyLatest)

  let listItemResult
  const articlesResponse = {}
  if(cacheHit) {
    listItemResult = cacheHit
  } else {
    const nonPinnedRequest = makeListArtclesByVisibilityRequest(visibility, nextToken, 200, false)
    const pinnedRequest = makeListArtclesByVisibilityRequest(visibility, nextToken, 200, true)
    const [nonPinnedResult, pinnedResult] = await Promise.all([
      sendRequest(nonPinnedRequest),
      // Only fetch pinned articles when fetching the first set
      startIndex === 0 ? sendRequest(pinnedRequest) : Promise.resolve({value:[]})
    ])

    // Make sure values are arrays
    pinnedResult.value = pinnedResult.value || []
    nonPinnedResult.value = nonPinnedResult.value || []

    // There will always be one key since this function is provided with only one visibility
    const [pinPositionKey] = getPinKeysFromVisibiity(visibility)

    // Filter pinned result by visibility but for those not pinned in this category, but belonging to
    // this category, add them to the nonPinnedResult.value or else they get lost
    pinnedResult.value = pinnedResult?.value?.filter((article) => {
      const pinPositions = extractPinPositions(article)
      const match = pinPositions[pinPositionKey] >= 0
      if(!match && fieldsMatchVisibility(article.fields, visibility)) {
        nonPinnedResult.value.push(article)
      }
      return match
    })
    // Sort pinned by pin position
    pinnedResult?.value?.sort((a,b) => {
      const aPins = extractPinPositions(a)
      const bPins = extractPinPositions(b)
      return parseInt(aPins[pinPositionKey]) - parseInt(bPins[pinPositionKey])
    })

    if(pinnedResult?.value) pinnedResult.value = filterArticlesOnPublished(pinnedResult.value, published)

    // Sort non-pinned by date
    nonPinnedResult.value = filterArticlesOnPublished(nonPinnedResult.value, published)

    // Insert pinned articles at correct positions
    const combinedValue = insertPinned(nonPinnedResult?.value || [], pinnedResult?.value || [], pinPositionKey)
    listItemResult = { value: combinedValue } // nonPinnedResult 
    cache.setItem(cacheKey1,{...listItemResult})
  }

  if(!listItemResult?.value?.length) return

  const articleListItems = listItemResult.value
  articlesResponse.totalSize = articleListItems.length
  articlesResponse.nextLink = listItemResult['@odata.nextLink']

  const endIndex = Math.min(
    articleListItems.length,
    startIndex + limit
  )
  const articles1 = []

  for(let i = startIndex; i < endIndex; i++) {
    articles1.push(await mapPage(articleListItems[i], null, false, visibility))
  }

  yield {...articlesResponse, articles: articles1}

  const batchBody = { requests: [] }
  for(let i = startIndex; i < endIndex; i++) {
    const articleListItem = articleListItems[i] 
    const contentType = articleListItem?.contentType?.name
    const pageRequest =
      contentType === 'Site Page' ? makeGetArticleSitePageByIdRequest(eTagToGuid(articleListItem.eTag))
      : makeGetNewsLinkPageByIdRequest(eTagToGuid(articleListItem.eTag))
    batchBody.requests.push({
      id: `${i}`,
      method: pageRequest.method,
      url: pageRequest.path
    })
  }

  if(!batchBody.requests.length) return

  const batchRequest = makeBatchRequest(batchBody.requests, 'beta')
  // const batchHash = strToB64(JSON.stringify(batchRequest))
  const cacheKey2 = `list-articles-site-pages-${makeCacheKey(batchRequest)}`
  const cacheHit2 = cache.getItem(cacheKey2)
  const batchResponse = cacheHit2 ?? await sendRequest(batchRequest)
  if(!cacheHit2) cache.setItem(cacheKey2, batchResponse)

  const combinedArticlePromises = ([...articleListItems]).slice(startIndex, endIndex).map(articleListItem => {
    const articleSitePage = batchResponse?.responses
      ?.find(response => eTagToGuid(response?.body?.eTag) === eTagToGuid(articleListItem.eTag)) || {body: {}}
    return mapPage({...articleSitePage?.body, ...articleListItem}, cache, true, visibility)
  })
  const articles2 = await Promise.all(combinedArticlePromises)

  const finalResult = {...articlesResponse, articles: articles2, totalLoaded: endIndex}
  if(cacheHitLatest) {
    Cache.setItem(cacheKeyLatest, {
      ...cacheHitLatest,
      articles: [...cacheHitLatest.articles, ...articles2],
      totalLoaded: endIndex
    })
  } else {
    Cache.setItem(cacheKeyLatest, finalResult)
  }
  yield finalResult
}

export const getDriveItemListItemDetail = async (listId, itemId) => {
  const { siteId } = getSiteConfig()
  const token = await getAccessToken()
  const detailedUrl = `https://graph.microsoft.com/beta/sites/${siteId}/lists/${listId}/items/${itemId}?$expand=fields,driveItem`
  const options = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  }
  const { data } = await axios.get(detailedUrl, options)
  return data
}

export const getListItem = async (listId, listItemId, expand = ['fields']) => {
  const { siteId } = getSiteConfig()
  const token = await getAccessToken()
  const expandParams = expand.length ? `?$expand=${expand.join(',')}` : ''
  const detailedUrl = `https://graph.microsoft.com/beta/sites/${siteId}/lists/${listId}/items/${listItemId}${expandParams}`
  const options = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  }
  const { data } = await axios.get(detailedUrl, options)

  return data
}

export const getArticleById = async (id) => {
  const { siteId } = getSiteConfig()
  const token = window.sessionStorage.mockGraphToken ?? (await getToken()).accessToken
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/pages/${id}/microsoft.graph.sitePage?&$expand=canvasLayout`
  const options = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  }
  const { data } = await axios.get(url, options)
  const article = data
  if(!article) return {}
  const image = article.bannerImageWebUrl ?? article.thumbnailWebUrl.indexOf('getpreview.ashx') > -1
    ? `${article.thumbnailWebUrl}&resolution=6`
    : article.thumbnailWebUrl.replace(/large.jpg/, 'xxlarge.jpg')
  return {
    ...article,
    date: new Date(article.createdDateTime).toLocaleDateString(),
    image,
  }
}

export const getPageListCategories = async (category) => {
  const { siteId, listIds } = getSiteConfig()
  const token = await getAccessToken()
  const options = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  }
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listIds.pages}/columns`
  const response = await createRetryAxios().get(url, options);
  const data = response?.data
  if(!data) return []
  const { value } = data
  const categoryColumnDef = value.find(v => v.displayName === category)
  return categoryColumnDef.choice.choices
}

export const getColumnChoices = async (listId, columnName) => {
  const { siteId } = getSiteConfig()
  const token = await getAccessToken()
  const options = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  }
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listId}/columns`
  const response = await createRetryAxios().get(url, options)
  const data = response?.data
  if(!data) return []
  const { value } = data
  const tagColumnDef = value.find(v => v.displayName === columnName)
  return tagColumnDef.choice.choices
}


export const getPageTagChoices = async () => {
  const { listIds } = getSiteConfig()
  return await getColumnChoices(listIds.pages, 'QSEPortalTagChoices')
}

export const getOuCornerCategoryChoices = async () => {
  const { listIds } = getSiteConfig()
  return await getColumnChoices(listIds.pages, 'QSEPortalOUCornerCategory')
}

export const searchLists = async (query) => {
  const { urls } = getSiteConfig()
  const token = await getAccessToken()
  const url = `https://graph.microsoft.com/v1.0/search/query`
  const body = {
    "requests": [
      {
        "entityTypes": [
          "listItem"
        ],
        "query": {
          "queryString": `${query} path:"${urls.baseSite}"`
        }
      }
    ]
  }
  const response = await createRetryAxios().post(url, body, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
  const data = response?.data
  if(!data) return []
  return data?.value?.[0]?.hitsContainers?.[0]?.hits || []
}

export const getPageTemplateCanvasLayout = async (templatePageId) => {
  const { siteId } = getSiteConfig()
  const token = await getAccessToken()
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/pages/${templatePageId}/microsoft.graph.sitePage?$expand=canvasLayout`
   const response = await createRetryAxios().get(url, {
    headers: {
      // Accept: 'application/json;odata.metadata=none',
      Authorization: `Bearer ${token}`
    }
  })
  const data = response?.data
  if(!data) return []
  const { canvasLayout } = data
  return removeODataContext(canvasLayout)
}

export const createArticle = async (article) => {
  const { siteId } = getSiteConfig()
  const token = await getAccessToken()
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/pages`
  
  const response = await createRetryAxios(true).post(url, article, {
    headers: {
      // Accept: 'application/json;odata.metadata=none',
      Authorization: `Bearer ${token}`
    }
  })
  const data = response?.data
  return data
}

export const getArticleLinkImageUrl = async (url, base64 = false) => {
  try {
    const token = (await getToken()).idToken
    const serviceUrl = `${ApiGateway.sharepoint}/get-image-preview`
    const response = await axios.post(serviceUrl, { url, base64 }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data
  } catch (err) {
    console.error('Failed to fetch or parse the page:', err.message);
    return null;
  }
}

export function base64ToFile(base64, filename, fallbackMime) {
  let mime = fallbackMime || "application/octet-stream";
  let b64Data = base64;

  // If it’s a data-URI:  "data:image/png;base64,AAAA"
  if (base64.startsWith("data:")) {
    const parts = base64.split(",");
    const mimeMatch = parts[0].match(/:(.*?);/);
    if (mimeMatch) mime = mimeMatch[1];
    b64Data = parts[1];               // actual Base-64 payload
  }

  // Decode Base-64 → binary string → Uint8Array
  const binary = atob(b64Data);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);

  // Wrap bytes in a File object
  return new File([bytes], filename, {
    type: mime,
    lastModified: Date.now(),
  });
}

export async function createNewsLinkPageWithImageBase64(title, newsWebUrl, imageBase64, fields, id) {
  const res = await createNewsLinkPage(title, newsWebUrl, undefined, base64ToFile(imageBase64), fields, id)
  return res
}

export async function createNewsLinkPageWithImageUrl(title, newsWebUrl, imageUrl, fields, id) {
  const res = await createNewsLinkPage(title, newsWebUrl, imageUrl, undefined, fields, id)
  return res
}

export async function createNewsLinkPageWithFile(title, newsWebUrl, file, fields, id) {
  const res = await createNewsLinkPage(title, newsWebUrl, undefined, file, fields, id)
  return res
}

export async function createNewsLinkPage(title, newsWebUrl, imageUrl, file, fields, id = undefined) {
  const { siteId, listIds } = getSiteConfig()
  const accessToken = await getAccessToken()
  let imageBytes, mime
  if(imageUrl) {
    const imgResponse = await axios.get(imageUrl, { responseType: "arraybuffer" });
    imageBytes = imgResponse.data
    mime = imgResponse.headers["content-type"] || "image/jpeg";
  } else if(file) {
    imageBytes = file
    mime = file.type
  }

  /* Build the JSON part (matches Graph docs exactly) */
  const metadata = {
    "@odata.type": "#microsoft.graph.newsLinkPage",
    "@microsoft.graph.bannerImageWebUrlContent": "name:content",
    title,
    newsWebUrl,
    description: title
  };

  try {
    const decodedNewsWebUrl = decodeURI(newsWebUrl)
    metadata.newsWebUrl = decodedNewsWebUrl
  } catch(err) {

  }

  /* Assemble multipart form data */
  const form = new FormData();
  // Part 1 – JSON metadata
  form.append(
    "request",
    new Blob([JSON.stringify(metadata)], { type: "application/json" }),
    "metadata.json"
  );

  // Part 2 – raw image bytes
  form.append(
    "content",
    new Blob([imageBytes], { type: mime }),
    "banner" + (mime === "image/png" ? ".png" : ".jpg")
  );

  /* POST to Graph */
  const url = `https://graph.microsoft.com/beta/sites/${siteId}/pages${id ? '/' + id + '/microsoft.graph.newsLinkPage': ''}`;

  const method = id ? createRetryAxios().patch : createRetryAxios().post
  const res = await method(url, form, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Prefer: "include-unknown-enum-members",
      // Do NOT set Content-Type manually—axios adds the boundary for you
    },
    maxBodyLength: Infinity, // large images okay
  });

  await updateListItemFields(listIds.pages, res.data.id, fields)
  await publishArticle(res.data.id, 'newsLinkPage')
  return res.data; // → created newsLinkPage object
}

export const createArticleLink = async (title, newsWebUrl, image) => {
  const { siteId } = getSiteConfig()
  const article = {
    "@odata.type": "#microsoft.graph.newsLinkPage",
    title,
    newsWebUrl
  }
  const token = await getAccessToken()
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/pages`
  const response = await createRetryAxios().post(url, article, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
  const data = response?.data
  return data
}

export const deleteArticle = async (article) => {
  const { siteId } = getSiteConfig()
  const token = await getAccessToken()
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/pages/${article.guid || article.id}`
  const response = await createRetryAxios().delete(url, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
  const data = response?.data
  return data
}

export const createArticleFromTemplate = async (template, article, fields, user) => {
  const { listIds, templateIds } = getSiteConfig()
  if (!Object.keys(templateIds).includes(template)) {
    throw new Error(`Error: ${template} is not one of: ${Object.keys(templateIds).join(', ')}`)
  }
  const templatePageId = templateIds[template]
  const canvasLayout = await getPageTemplateCanvasLayout(templatePageId)
  const canvasLayoutProperties = canvasLayout
    ?.horizontalSections
    ?.[0]
    ?.columns
    ?.[0]
    ?.webparts
    ?.[0]
    ?.data
    ?.properties
  if(canvasLayoutProperties?.["authorByline@odata.type"]) {
      canvasLayoutProperties.authors = [
        {
          id: user.email,
          email: user.email,
          name: user.name,
          role: '',
          aadObjectId: user.uniqueId
        }
      ]
      canvasLayoutProperties.authorByline = [user.email]
    }

  const newArticle = await createArticle({ ...article, canvasLayout })
  const fieldsResult = await updateListItemFields(listIds.pages, newArticle.id, fields)
  return newArticle
}

export const updateListItemFields = async (listId, itemId, fields) => {
  const { siteId } = getSiteConfig()
  const token = await getAccessToken()
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listId}/items/${itemId}/fields`
  const { data } = await axios.patch(url, fields, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
  return data
}

const undefinedToNull = (val) => {
  if(val === undefined) return null
  return val
}

export const updateArticleMetadata = async (articleId, metadata, shouldPublish = true) => {
  const { listIds } = getSiteConfig()
  const {
    news,
    featured,
    ouCorner,
    highlighted,
    ouCornerCategory,
    tags,
    pinPositions
  } = metadata

  const allTags = [...tags?.map(tag => `tag:${tag}`)]

  if (featured) allTags.push('visibility:Featured')
  if (news) allTags.push('visibility:News')
  if (ouCornerCategory) allTags.push(`visibility:OUCorner::${ouCornerCategory}`)
  if (highlighted) allTags.push('visibility:OUCorner::Highlighted')

  const mapped = {
    QSEPortalFeatured: undefinedToNull(featured),
    QSEPortalNews: undefinedToNull(news),
    QSEPortalOUCorner: undefinedToNull(ouCorner),
    QSEPortalOUCornerCategory: undefinedToNull(ouCornerCategory),
    QSEPortalHighlighted: undefinedToNull(highlighted),
    QSEPortalTags: allTags.join(','),
    QSEPortalPinPositions: pinPositions
  }

  await updateListItemFields(listIds.pages, articleId, mapped)
  if(shouldPublish) {
    await publishArticle(articleId)
  }
}

const getArticleType = async (articleId, _token) => {
  const { siteId } = getSiteConfig()
  const token = _token ?? await getAccessToken()
  const url = `https://graph.microsoft.com/beta/sites/${siteId}/pages/${articleId}`
  const response = await createRetryAxios().get(url, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
  const data = response?.data
  return data?.['@odata.type'] || data.value?.[0]?.['@odata.type']
}

export const publishArticle = async (articleId, graphType) => {
  const { siteId } = getSiteConfig()
  const token = await getAccessToken()
  if(!graphType) {
    const _type = await getArticleType(articleId, token)
    graphType = _type.split('.')[2]
  }
  const url = `https://graph.microsoft.com/beta/sites/${siteId}/pages/${articleId}/microsoft.graph.${graphType ?? 'sitePage'}/publish`
  const response = await createRetryAxios().post(url, undefined, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
  const data = response?.data
  return data
}

export const getArticleShareHTML = async(pageId, recipients, link) => {
  try {
    const token = (await getToken()).idToken
    const serviceUrl = `${ApiGateway.sharepoint}/article-share-html`
    const siteConfig = getSiteConfig()

    if(Stage !== 'prod') {
      const sharepointToken = await getAccessToken()
      siteConfig.graphToken = sharepointToken
    }

    const response = await axios.post(serviceUrl, {
      pageId,
      recipients,
      siteConfig,
      link
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data
  } catch (err) {
    console.error('Failed to fetch or parse the page:', err.message);
    return null;
  }
}

export const getEmailDistributionList = async(recipients) => {
  try {
    const token = (await getToken()).idToken
    const serviceUrl = `${ApiGateway.sharepoint}/email-list`
    const siteConfig = getSiteConfig()

    if(Stage !== 'prod') {
      const sharepointToken = await getAccessToken()
      siteConfig.graphToken = sharepointToken
    }

    const response = await axios.post(serviceUrl, recipients, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data
  } catch (err) {
    console.error('Failed to fetch or parse the page:', err.message);
    return null;
  }
}

export const sendArticle = async(subject, bcc, html) => {
  try {
    const token = (await getToken()).idToken
    const serviceUrl = `${ApiGateway.sharepoint}/send-email`
    const siteConfig = getSiteConfig()

    if(Stage !== 'prod') {
      const sharepointToken = await getAccessToken()
      siteConfig.graphToken = sharepointToken
    }

    const response = await axios.post(serviceUrl, {
      subject,
      html,
      bcc
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data
  } catch (err) {
    console.error('Failed to fetch or parse the page:', err.message);
    return null;
  }
}